import type { MouseEventHandler } from 'react';
import { useCallback } from 'react';

import type { MessageTranslator } from '@oneflowab/pomes';
import { localize } from '@oneflowab/pomes';
import type { FormRenderProps } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import { checkAcl } from '@flow/libs/utils/acl';

import Button from '@app/components/button';
import { CancelButton } from '@app/components/buttons';
import Field from '@app/components/field';
import { DEFAULT_NODES, LiteRichTextField } from '@app/components/fields/lite-rich-text';
import CircularSpinner from '@app/components/icons/circular-spinner';
import { Note } from '@app/components/icons/note';
import SmallAdd from '@app/components/icons/small-add';
import { MenuItem } from '@app/components/menu-item';
import TextField from '@app/components/text-field';
import { T } from '@app/components/translation';
import ModalForm from '@app/hocs/modal-form';
import agreements from '@app/reducers/entities/agreements';

import style from './add-note.module.scss';

const TITLE_MAX_LENGTH = 200;

const isContentEmpty = (values: Record<string, any>) =>
  values.content === JSON.stringify(DEFAULT_NODES);

export type Props = {
  message: MessageTranslator;
  agreement: Oneflow.Agreement;
  isRenderedInNoteList?: boolean;
};

type FormValues = {
  name?: string;
  content: string;
};

export const AddNoteComponent = ({ message, agreement, isRenderedInNoteList }: Props) => {
  const isAllowedToAddNote = checkAcl(agreement.acl, 'agreement:note:create');
  const dispatch = useDispatch();

  const createState = useSelector((state) =>
    agreements.getCreateContractNoteSelector(state, {
      id: agreement.id,
    }),
  );

  const resetCreateState = useCallback(() => {
    dispatch(
      agreements.createContractNoteReset({
        id: agreement.id,
      }),
    );
  }, [
    agreement.id,
    dispatch,
  ]);

  const fetchNotes = useCallback(() => {
    dispatch(
      agreements.fetchContractNotes({
        id: agreement.id,
      }),
    );
  }, [
    agreement.id,
    dispatch,
  ]);

  const addNote = useCallback(
    (note: FormValues) =>
      dispatch(
        agreements.createContractNote({
          id: agreement.id,
          data: {
            name: note.name,
            content: JSON.parse(note.content),
          },
          pipe: {
            onSuccess: fetchNotes,
          },
        }),
      ),
    [
      agreement.id,
      dispatch,
      fetchNotes,
    ],
  );

  const getBody = useCallback(
    () => (
      <div className={style.Body}>
        <Field
          name="name"
          // @ts-ignore
          component={TextField}
          autoComplete="off"
          hideLabel
          maxLength={TITLE_MAX_LENGTH}
          label={
            message({
              id: 'Title',
              comment: 'Contract note title label',
            }) as string
          }
          placeholder={
            message({
              id: 'Title',
              comment: 'Contract note title placeholder',
            }) as string
          }
          customClass={style.TitleField}
        />
        <div className={style.ContentField}>
          <LiteRichTextField
            name="content"
            placeholder={
              message({
                id: 'Lorem ipsum',
                comment: 'Contract note content placeholder',
              }) as string
            }
            elementClassNames={{
              container: style.LiteRichTextContainer,
              editable: style.LiteRichTextEditable,
              toolbar: style.LiteRichTextToolbar,
            }}
          />
        </div>
      </div>
    ),
    [
      message,
    ],
  );

  const getActions = useCallback(
    ({
      formProps: { pristine, validating, hasValidationErrors, values, handleSubmit },
      closeConfirmation,
    }: {
      formProps: FormRenderProps;
      closeConfirmation: () => void;
    }) => {
      const isSubmitDisabled =
        pristine ||
        validating ||
        hasValidationErrors ||
        createState.loading ||
        isContentEmpty(values);

      return (
        <div className={style.DocumentNoteActions}>
          <CancelButton onClick={closeConfirmation} />
          <Button
            type="submit"
            disabled={isSubmitDisabled}
            kind="primary"
            icon={createState.loading ? CircularSpinner : undefined}
            onClick={handleSubmit}
          >
            <T
              _str="Add note"
              _comment="Label for 'Add note' action."
            />
          </Button>
        </div>
      );
    },
    [
      createState.loading,
    ],
  );

  const getChildren = useCallback(
    (onClick: MouseEventHandler) => (
      <MenuItem
        disabled={!isAllowedToAddNote}
        label={
          <T
            _str="Add note"
            _comment="Label for 'Add note' action"
          />
        }
        icon={isRenderedInNoteList ? <SmallAdd height="10px" /> : Note}
        className={isRenderedInNoteList ? style.AddNoteButton : undefined}
        onClick={onClick}
      />
    ),
    [
      isAllowedToAddNote,
      isRenderedInNoteList,
    ],
  );

  // Temporary: Remove once document notes is fully released
  if (!isAllowedToAddNote) {
    return null;
  }

  return (
    // @ts-ignore
    <ModalForm
      modalKey="add-note"
      title={
        <T
          _str="Add note"
          _comment="Title for 'Add note' modal"
        />
      }
      body={getBody()}
      formState={createState}
      showSuccessMessage
      onClose={resetCreateState}
      onSubmit={addNote}
      actions={getActions}
      initialValues={{
        name: undefined,
        content: JSON.stringify(DEFAULT_NODES),
      }}
    >
      {getChildren}
    </ModalForm>
  );
};

export const AddNote = localize(AddNoteComponent) as unknown as (
  props: Omit<Props, 'message'>,
) => JSX.Element;
